/**
 * All USDC amounts in this codebase are integer base units (6 decimals) represented
 * as `bigint`. Never use `number`/floating-point for money — see non_negotiable_architecture.
 */
export const USDC_DECIMALS = 6;
export const DEFAULT_PLATFORM_FEE_BPS = 1000n; // 10%
export const BPS_DENOMINATOR = 10000n;
export class InvalidAmountError extends Error {
    constructor(message) {
        super(message);
        this.name = "InvalidAmountError";
    }
}
/** Ceiling integer division for non-negative bigints: ceil(a / b). */
export function ceilDiv(a, b) {
    if (b <= 0n)
        throw new InvalidAmountError("divisor must be positive");
    if (a < 0n)
        throw new InvalidAmountError("dividend must be non-negative");
    return (a + b - 1n) / b;
}
/**
 * Mirrors the Solidity contract's fee math exactly: platformFee = ceil(principal * bps / 10_000),
 * totalDue = principal + platformFee. Both the API and the contract must agree bit-for-bit;
 * this function (and the Solidity implementation) are the two canonical implementations and
 * are cross-checked in tests.
 */
export function computeFeeQuote(principal, platformFeeBps = DEFAULT_PLATFORM_FEE_BPS) {
    if (principal <= 0n)
        throw new InvalidAmountError("principal must be positive");
    if (platformFeeBps <= 0n)
        throw new InvalidAmountError("platformFeeBps must be positive");
    const platformFee = ceilDiv(principal * platformFeeBps, BPS_DENOMINATOR);
    return {
        principal,
        platformFeeBps,
        platformFee,
        totalDue: principal + platformFee,
    };
}
export function assertMinListingPrice(principal, minPrice) {
    if (principal < minPrice) {
        throw new InvalidAmountError(`listing price ${principal} is below the configured minimum ${minPrice}`);
    }
}
/** Formats integer USDC base units as a human string, e.g. 1500000n -> "1.50". No floats used. */
export function formatUsdcBaseUnits(amount) {
    const negative = amount < 0n;
    const abs = negative ? -amount : amount;
    const divisor = 10n ** BigInt(USDC_DECIMALS);
    const whole = abs / divisor;
    const fraction = (abs % divisor).toString().padStart(USDC_DECIMALS, "0");
    const trimmedFraction = fraction.replace(/0+$/, "").padEnd(2, "0");
    return `${negative ? "-" : ""}${whole.toString()}.${trimmedFraction}`;
}
/** Parses a decimal USDC string (e.g. "12.50") into integer base units. Throws on invalid input. */
export function parseUsdcToBaseUnits(input) {
    const trimmed = input.trim();
    if (!/^\d+(\.\d{1,6})?$/.test(trimmed)) {
        throw new InvalidAmountError(`invalid USDC amount: ${input}`);
    }
    const [whole = "0", fraction = ""] = trimmed.split(".");
    const paddedFraction = fraction.padEnd(USDC_DECIMALS, "0");
    return BigInt(whole) * 10n ** BigInt(USDC_DECIMALS) + BigInt(paddedFraction || "0");
}
//# sourceMappingURL=money.js.map